import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, CalendarPlus, Pencil, Trash2, CalendarDays, Store, Info, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { PageHeader, SectionCard, ErrorState } from "@/components/exec/Primitives";
import { HolidayLegend } from "@/components/exec/Charts";
import { useCalendar, useInvalidateAnalytics } from "@/lib/queries";
import { useScope } from "@/lib/scope";
import { useApp } from "@/lib/app";
import { api, errorMessage } from "@/lib/api";
import { fmtDate, fmtDateLong } from "@/lib/format";
import { cn } from "@/lib/utils";

/* ------------------------------------------------------------------ date helpers (local, ISO strings) */
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const fromIso = (s) => new Date(`${s}T00:00:00`);
const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};
const monthStart = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const monthEnd = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const MONTH_LABEL = (d) => d.toLocaleDateString("en-MY", { month: "long", year: "numeric" });
const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/** marker colour per event (tailwind classes, resolved from tokens) */
const dotClass = (e) => {
  if (e.type === "public_holiday") return { MY: "bg-holiday-my", SG: "bg-holiday-sg", JOHOR: "bg-holiday-johor" }[e.market] || "bg-holiday-my";
  return { school_holiday: "bg-holiday-school", payday: "bg-holiday-payday", demand: "bg-holiday-demand", custom: "bg-holiday-custom" }[e.type] || "bg-holiday-custom";
};
const FILTER_KEYS = [
  { key: "MY", label: "MY holiday", test: (e) => e.type === "public_holiday" && e.market === "MY", cls: "bg-holiday-my" },
  { key: "SG", label: "SG holiday", test: (e) => e.type === "public_holiday" && e.market === "SG", cls: "bg-holiday-sg" },
  { key: "JOHOR", label: "Johor holiday", test: (e) => e.type === "public_holiday" && e.market === "JOHOR", cls: "bg-holiday-johor" },
  { key: "school", label: "School holidays", test: (e) => e.type === "school_holiday", cls: "bg-holiday-school" },
  { key: "payday", label: "Payday", test: (e) => e.type === "payday", cls: "bg-holiday-payday" },
  { key: "demand", label: "Travel windows", test: (e) => e.type === "demand", cls: "bg-holiday-demand" },
  { key: "custom", label: "Your events", test: (e) => e.type === "custom", cls: "bg-holiday-custom" },
];
const TYPE_LABEL = { public_holiday: "Public holiday", school_holiday: "School holiday", payday: "Payday", demand: "Travel demand", custom: "Your event" };
const MARKET_LABEL = { MY: "Malaysia", SG: "Singapore", JOHOR: "Johor", CN: "China", ZAPIN: "Zapin" };

/* ------------------------------------------------------------------ add / edit dialog */
const EventDialog = ({ open, onOpenChange, event, stores, onSaved }) => {
  const isEdit = !!event?.id;
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [range, setRange] = useState({});
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  const [calOpen, setCalOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    setName(event?.name || "");
    setNote(event?.note || "");
    setRange(event?.start ? { from: fromIso(event.start), to: fromIso(event.end || event.start) } : {});
    setPicked(event?.stores || []);
  }, [open, event]);

  const submit = async (e) => {
    e.preventDefault();
    if (name.trim().length < 2) return toast.error("Give the event a name (2+ characters)");
    if (!range?.from) return toast.error("Pick the event date(s)");
    const body = { name: name.trim(), start: iso(range.from), end: iso(range.to || range.from), note: note.trim(), stores: picked };
    setBusy(true);
    try {
      if (isEdit) await api.updateEvent(event.id, body);
      else await api.createEvent(body);
      toast.success(isEdit ? "Event updated" : "Event added to the calendar", { description: "It now shows on every chart as a marker." });
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(errorMessage(err, "Could not save the event"));
    } finally {
      setBusy(false);
    }
  };
  const togglePick = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg bg-popover/95 backdrop-blur-xl" data-testid="calendar-event-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{isEdit ? "Edit event" : "Add an event"}</DialogTitle>
          <DialogDescription>Store promotions, mall closures, roadshows, bus-tour arrivals - anything that explains a spike or a dip. Visible to everyone and used by the AI as context.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="ev-name">Name</Label>
            <Input id="ev-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Stulang 20% whisky weekend" maxLength={120} data-testid="calendar-event-name" required />
          </div>
          <div className="space-y-1.5">
            <Label>Dates</Label>
            <Popover open={calOpen} onOpenChange={setCalOpen}>
              <PopoverTrigger asChild>
                <Button type="button" variant="outline" className="w-full justify-start font-mono text-xs h-10 bg-card/70" data-testid="calendar-event-dates">
                  <CalendarDays size={14} className="text-gold mr-2" />
                  {range?.from ? `${fmtDateLong(iso(range.from))}${range.to && iso(range.to) !== iso(range.from) ? ` → ${fmtDateLong(iso(range.to))}` : ""}` : "Pick a day or a range"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-2 bg-popover/95 backdrop-blur-xl" align="start">
                <Calendar mode="range" numberOfMonths={1} selected={range} onSelect={(r) => setRange(r || {})} defaultMonth={range?.from || new Date()} data-testid="calendar-event-picker" />
                <div className="flex justify-end px-1 pb-1"><Button type="button" size="sm" className="h-7 text-xs" onClick={() => setCalOpen(false)} data-testid="calendar-event-dates-done">Done</Button></div>
              </PopoverContent>
            </Popover>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ev-note">Note <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <Textarea id="ev-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={400} placeholder="What happened and why it matters for footfall or sales" data-testid="calendar-event-note" />
          </div>
          <div className="space-y-1.5">
            <Label>Stores <span className="text-muted-foreground font-normal">(leave empty for all stores)</span></Label>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 rounded-lg border border-border/60 bg-muted/10 p-3 max-h-40 overflow-y-auto scroll-thin" data-testid="calendar-event-stores">
              {stores.map((s) => (
                <label key={s.id} className="flex items-center gap-2 text-xs cursor-pointer">
                  <Checkbox checked={picked.includes(s.id)} onCheckedChange={() => togglePick(s.id)} data-testid={`calendar-event-store-${s.id}`} className="data-[state=checked]:bg-gold data-[state=checked]:border-gold" />
                  <span className="truncate">{s.name}</span>
                </label>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="calendar-event-submit">{busy ? "Saving…" : isEdit ? "Save changes" : "Add event"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

/* ------------------------------------------------------------------ page */
export default function CalendarEvents() {
  const { scope } = useScope();
  const { scopes, anchor, user } = useApp();
  const invalidate = useInvalidateAnalytics();
  const [month, setMonth] = useState(() => monthStart(anchor ? fromIso(anchor) : new Date()));
  const [selected, setSelected] = useState(null);
  const [enabled, setEnabled] = useState(() => new Set(FILTER_KEYS.map((f) => f.key)));
  const [dialog, setDialog] = useState({ open: false, event: null });

  useEffect(() => {
    document.title = "Zapin | Calendar & Events";
  }, []);

  // grid covers the Monday before the 1st through the Sunday after the last day
  const gridStart = useMemo(() => addDays(month, -((month.getDay() + 6) % 7)), [month]);
  const gridEnd = useMemo(() => {
    const e = monthEnd(month);
    return addDays(e, (7 - ((e.getDay() + 6) % 7) - 1) % 7);
  }, [month]);
  const cells = useMemo(() => {
    const out = [];
    for (let d = new Date(gridStart); d <= gridEnd; d = addDays(d, 1)) out.push(iso(d));
    return out;
  }, [gridStart, gridEnd]);

  const q = useCalendar({ start: iso(gridStart), end: iso(gridEnd), scope });
  const data = q.data;
  const today = anchor || iso(new Date());
  const stores = useMemo(() => (scopes || []).filter((s) => s.kind === "site"), [scopes]);

  const visible = useMemo(() => (data?.events || []).filter((e) => FILTER_KEYS.some((f) => enabled.has(f.key) && f.test(e))), [data, enabled]);
  const byDay = useMemo(() => {
    const m = {};
    visible.forEach((e) => {
      for (let d = fromIso(e.start); d <= fromIso(e.end); d = addDays(d, 1)) {
        const k = iso(d);
        (m[k] = m[k] || []).push(e);
      }
    });
    return m;
  }, [visible]);
  const monthEvents = useMemo(() => {
    const ms = iso(month);
    const me = iso(monthEnd(month));
    return visible.filter((e) => e.end >= ms && e.start <= me).sort((a, b) => (a.start < b.start ? -1 : a.start > b.start ? 1 : 0));
  }, [visible, month]);
  const listed = selected ? byDay[selected] || [] : monthEvents;

  const toggle = (key) =>
    setEnabled((s) => {
      const n = new Set(s);
      if (n.has(key)) n.delete(key);
      else n.add(key);
      return n;
    });

  const remove = async (ev) => {
    try {
      await api.deleteEvent(ev.id);
      toast.success("Event removed");
      invalidate();
    } catch (e) {
      toast.error(errorMessage(e, "Could not remove the event"));
    }
  };

  const counts = useMemo(() => {
    const c = {};
    FILTER_KEYS.forEach((f) => (c[f.key] = (data?.events || []).filter(f.test).filter((e) => e.end >= iso(month) && e.start <= iso(monthEnd(month))).length));
    return c;
  }, [data, month]);

  return (
    <div className="space-y-6 md:space-y-8" data-testid="calendar-page">
      <PageHeader
        eyebrow="Calendar & events"
        title="The trading calendar behind the numbers"
        subtitle="Malaysia, Singapore and Johor public holidays, MY and SG school holidays, month-end paydays, Chinese travel windows and your own events. Every marker here is the same marker on the charts."
        testId="calendar-header"
        actions={<Button size="sm" className="h-9 gap-1.5" onClick={() => setDialog({ open: true, event: null })} data-testid="calendar-add-event-button"><CalendarPlus size={14} /> Add event</Button>}
      />

      {q.isError && <ErrorState message={errorMessage(q.error)} onRetry={() => q.refetch()} testId="calendar-error" />}

      <div className="flex flex-wrap gap-1.5" data-testid="calendar-filters">
        {FILTER_KEYS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => toggle(f.key)}
            aria-pressed={enabled.has(f.key)}
            data-testid={`calendar-filter-${f.key}`}
            className={cn("inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs focus-gold transition-colors duration-200", enabled.has(f.key) ? "border-border/70 bg-card text-foreground" : "border-border/40 bg-transparent text-muted-foreground/60 line-through")}
          >
            <span className={cn("h-2 w-2 rounded-full", f.cls, !enabled.has(f.key) && "opacity-40")} />
            {f.label}
            <span className="font-mono text-[10px] text-muted-foreground">{counts[f.key] ?? 0}</span>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <SectionCard
          className="lg:col-span-7"
          testId="calendar-grid-card"
          title={
            <span className="inline-flex items-center gap-2">
              <Button variant="outline" size="icon" className="h-8 w-8 bg-card/70" onClick={() => setMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1))} aria-label="Previous month" data-testid="calendar-prev-month"><ChevronLeft size={15} /></Button>
              <span className="min-w-[160px] text-center" data-testid="calendar-month-label">{MONTH_LABEL(month)}</span>
              <Button variant="outline" size="icon" className="h-8 w-8 bg-card/70" onClick={() => setMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1))} aria-label="Next month" data-testid="calendar-next-month"><ChevronRight size={15} /></Button>
            </span>
          }
          actions={
            <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => { setMonth(monthStart(fromIso(today))); setSelected(null); }} data-testid="calendar-today">Today</Button>
          }
        >
          <div className="grid grid-cols-7 gap-1 text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-1 px-0.5">
            {DOW.map((d) => <div key={d} className={cn("text-center py-1", (d === "Sat" || d === "Sun") && "text-gold/80")}>{d}</div>)}
          </div>
          {!data ? (
            <Skeleton className="h-[360px] w-full rounded-lg" />
          ) : (
            <div className="grid grid-cols-7 gap-1" role="grid" data-testid="holiday-calendar">
              {cells.map((d) => {
                const inMonth = fromIso(d).getMonth() === month.getMonth();
                const evs = byDay[d] || [];
                const f = data.flags?.[d];
                const isToday = d === today;
                const isSel = d === selected;
                return (
                  <button
                    key={d}
                    type="button"
                    role="gridcell"
                    onClick={() => setSelected(isSel ? null : d)}
                    data-testid={`calendar-day-${d}`}
                    data-events={evs.length}
                    className={cn(
                      "relative min-h-[52px] md:min-h-[64px] rounded-lg border p-1.5 text-left focus-gold transition-colors duration-200 flex flex-col",
                      inMonth ? "border-border/60 bg-muted/10 hover:bg-muted/25" : "border-transparent bg-transparent text-muted-foreground/40",
                      f?.is_weekend && inMonth && "bg-gold/[0.04]",
                      isSel && "border-gold/60 bg-gold/10",
                      isToday && "ring-1 ring-gold/60"
                    )}
                  >
                    <span className={cn("font-mono text-xs", isToday && "text-gold font-medium")}>{fromIso(d).getDate()}</span>
                    {f?.long_weekend && inMonth && <span className="absolute top-1.5 right-1.5 text-[9px] uppercase tracking-wider text-gold/80 hidden md:inline">long</span>}
                    <span className="mt-auto flex items-center gap-1 flex-wrap">
                      {evs.slice(0, 3).map((e, i) => <span key={`${e.id}-${i}`} className={cn("h-1.5 w-1.5 rounded-full", dotClass(e), e.type === "payday" && "ring-1 ring-holiday-payday/60 bg-transparent")} />)}
                      {evs.length > 3 && <span className="text-[9px] font-mono text-muted-foreground">+{evs.length - 3}</span>}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
          <HolidayLegend className="mt-4" />
        </SectionCard>

        <SectionCard
          className="lg:col-span-5"
          title={selected ? fmtDateLong(selected) : `${MONTH_LABEL(month)} · ${monthEvents.length} event${monthEvents.length === 1 ? "" : "s"}`}
          subtitle={selected ? "Everything on this day. Click the day again to see the whole month." : "Chronological. Your events can be edited; built-in holidays cannot."}
          testId="calendar-list-card"
          actions={selected ? <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => setSelected(null)} data-testid="calendar-clear-day">Whole month</Button> : null}
        >
          {!data ? (
            <div className="space-y-2">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-14 w-full" />)}</div>
          ) : listed.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border/70 bg-muted/10 p-5 text-sm text-muted-foreground" data-testid="calendar-list-empty">
              {selected ? "Nothing on this day." : "No events in this month with the current filters."}
              <div className="mt-2"><Button size="sm" variant="outline" className="h-8 gap-1.5" onClick={() => setDialog({ open: true, event: selected ? { start: selected, end: selected } : null })} data-testid="calendar-list-add"><CalendarPlus size={13} /> Add one</Button></div>
            </div>
          ) : (
            <ul className="space-y-2 max-h-[560px] overflow-y-auto scroll-thin pr-1" data-testid="calendar-event-list">
              {listed.map((e) => (
                <li key={`${e.id}-${e.start}`} className="rounded-lg border border-border/60 bg-muted/15 px-3 py-2.5 flex items-start gap-3" data-testid="calendar-event-item" data-type={e.type}>
                  <span className={cn("mt-1.5 h-2.5 w-2.5 rounded-full shrink-0", dotClass(e))} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium leading-snug" data-testid="calendar-event-name-text">{e.name}</span>
                      <Badge variant="outline" className="text-[10px] border-border/70 bg-background/60 text-muted-foreground">{TYPE_LABEL[e.type] || e.type}{e.market && e.type !== "custom" && e.type !== "payday" ? ` · ${MARKET_LABEL[e.market] || e.market}` : ""}</Badge>
                      {e.tentative && <Badge variant="outline" className="text-[10px] border-warning/40 bg-warning/10 text-warning">tentative</Badge>}
                    </div>
                    <div className="font-mono text-[11px] text-muted-foreground mt-0.5">{e.start === e.end ? fmtDateLong(e.start) : `${fmtDate(e.start)} – ${fmtDate(e.end)}`}{e.type === "public_holiday" && e.market === "MY" && e.observed_in_johor === false ? " · not observed in Johor" : ""}</div>
                    {e.note && <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{e.note}</p>}
                    {e.type === "custom" && (
                      <div className="text-[11px] text-muted-foreground mt-1 inline-flex items-center gap-1 flex-wrap">
                        <Store size={11} /> {e.stores?.length ? e.stores.map((id) => stores.find((s) => s.id === id)?.short || stores.find((s) => s.id === id)?.name || id).join(", ") : "all stores"}
                        {e.created_by && <span>· added by {e.created_by}</span>}
                      </div>
                    )}
                  </div>
                  {e.type === "custom" && (
                    <div className="flex items-center gap-0.5 shrink-0">
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground" aria-label="Edit event" onClick={() => setDialog({ open: true, event: e })} data-testid="calendar-event-edit"><Pencil size={13} /></Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-danger" aria-label="Delete event" data-testid="calendar-event-delete"><Trash2 size={13} /></Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent data-testid="calendar-event-delete-dialog">
                          <AlertDialogHeader>
                            <AlertDialogTitle className="font-display">Remove “{e.name}”?</AlertDialogTitle>
                            <AlertDialogDescription>The marker disappears from every chart and the AI stops using it as context.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Keep</AlertDialogCancel>
                            <AlertDialogAction className="bg-danger text-background hover:bg-danger/90" onClick={() => remove(e)} data-testid="calendar-event-delete-confirm">Remove</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </SectionCard>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-muted-foreground">
        <div className="flex items-start gap-2 rounded-lg border border-border/60 bg-muted/10 p-3"><Info size={13} className="text-gold mt-0.5 shrink-0" /><span>Johor observes Friday–Saturday weekends in some state offices, but the malls trade on a Saturday–Sunday pattern, so weekend shading follows Sat–Sun.</span></div>
        <div className="flex items-start gap-2 rounded-lg border border-border/60 bg-muted/10 p-3"><Sparkles size={13} className="text-gold mt-0.5 shrink-0" /><span>Public holidays become solid lines on charts, school holidays become shaded bands, paydays dashed lines and your events dots.</span></div>
        <div className="flex items-start gap-2 rounded-lg border border-border/60 bg-muted/10 p-3"><Store size={13} className="text-gold mt-0.5 shrink-0" /><span>Events tagged to specific stores only appear when those stores are in the selected scope. Signed in as {user?.name || user?.login}; anyone signed in can add events.</span></div>
      </div>

      <EventDialog open={dialog.open} onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))} event={dialog.event} stores={stores} onSaved={invalidate} />
    </div>
  );
}
